import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Title2,
  Body1,
  Spinner,
  Badge,
  Button,
  Input,
  Switch,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { fetchWithRetry } from "../lib/fetchWithRetry";
import EditUserDrawer from "../components/EditUserDrawer";
import AddUserDialog from "../components/AddUserDialog";

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  jobTitle: string | null;
  departmentId: string | null;
  departmentName: string | null;
  managerId: string | null;
  managerName: string | null;
  isActive: boolean;
  isExEmployee: boolean;
  needsEmailFix: boolean;
  roles: string[];
}

export interface Department {
  id: string;
  name: string;
}

interface AdminUsersResponse {
  users: AdminUser[];
  departments: Department[];
}

const useStyles = makeStyles({
  headerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalS
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
    flexWrap: "wrap"
  },
  search: {
    minWidth: "280px"
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  rolesCell: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS
  },
  emailCell: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }
});

function roleBadge(role: string) {
  if (role === "administrator") return <Badge key={role} appearance="filled" color="brand">admin</Badge>;
  if (role === "manager") return <Badge key={role} appearance="filled" color="informative">manager</Badge>;
  return <Badge key={role} appearance="outline">{role}</Badge>;
}

export default function AdminUsers() {
  const styles = useStyles();
  const toasterId = useId("admin-users-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const [data, setData] = useState<AdminUsersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);
  const [search, setSearch] = useState("");
  const [showExEmployees, setShowExEmployees] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setError(null);
    return fetchWithRetry("/api/manage-users", undefined, { onWarming: () => setWarming(true) })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${r.status}`);
        }
        return (await r.json()) as AdminUsersResponse;
      })
      .then((d) => { setData(d); setWarming(false); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    setData(null);
    setWarming(false);
    load();
  }, [load]);

  const filteredUsers = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.users.filter((u) => {
      // Single status: ex-employee toggles in/out of the default view. The legacy
      // is_active=0 + is_ex_employee=0 orphan rows surface in the active list so
      // they're visible and can be fixed via Edit.
      if (!showExEmployees && u.isExEmployee) return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) ||
        u.displayName.toLowerCase().includes(q) ||
        (u.departmentName ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, search, showExEmployees]);

  const editingUser = useMemo(
    () => (editingId && data ? data.users.find((u) => u.id === editingId) ?? null : null),
    [editingId, data]
  );

  if (error) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Admin · Users</Title2>
        <Body1 as="p" className={styles.error}>Error loading users: {error}</Body1>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Admin · Users</Title2>
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading…"}
        />
      </div>
    );
  }

  const placeholderCount = data.users.filter((u) => u.needsEmailFix && u.isActive).length;

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.headerRow}>
        <div>
          <Title2 as="h2" block>Admin · Users</Title2>
          <Body1 as="p">
            {data.users.length} total user{data.users.length === 1 ? "" : "s"}
            {placeholderCount > 0 && (
              <>
                {" · "}
                <strong>{placeholderCount} need an email fix</strong>
              </>
            )}
          </Body1>
        </div>
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          placeholder="Search by name, email, or department"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
        />
        <Switch
          label="Show ex-employees"
          checked={showExEmployees}
          onChange={(_, d) => setShowExEmployees(d.checked)}
        />
        <Button appearance="primary" onClick={() => setAdding(true)}>
          Add user
        </Button>
      </div>

      {filteredUsers.length === 0 ? (
        <div className={styles.empty}>
          <Body1>No users match the current filters.</Body1>
        </div>
      ) : (
        <Table aria-label="All users">
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: "320px" }}>Email</TableHeaderCell>
              <TableHeaderCell style={{ width: "180px" }}>Display name</TableHeaderCell>
              <TableHeaderCell style={{ width: "140px" }}>Department</TableHeaderCell>
              <TableHeaderCell style={{ width: "150px" }}>Manager</TableHeaderCell>
              <TableHeaderCell style={{ width: "170px" }}>Roles</TableHeaderCell>
              <TableHeaderCell style={{ width: "110px" }}>Status</TableHeaderCell>
              <TableHeaderCell style={{ width: "70px" }} aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div className={styles.emailCell}>
                    <span>{u.email}</span>
                    {u.needsEmailFix && (
                      <Badge appearance="filled" color="danger" size="small">Needs fix</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{u.displayName}</TableCell>
                <TableCell>{u.departmentName ?? "—"}</TableCell>
                <TableCell>{u.managerName ?? "—"}</TableCell>
                <TableCell>
                  <div className={styles.rolesCell}>
                    {u.roles.length === 0 ? (
                      <Body1 style={{ color: tokens.colorNeutralForeground3 }}>—</Body1>
                    ) : (
                      u.roles.map(roleBadge)
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {u.isExEmployee ? (
                    <Badge appearance="outline" color="severe">ex-employee</Badge>
                  ) : (
                    <Badge appearance="outline" color="success">active</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Button size="small" appearance="subtle" onClick={() => setEditingId(u.id)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <EditUserDrawer
        open={editingUser !== null}
        user={editingUser}
        allUsers={data.users}
        departments={data.departments}
        onClose={() => setEditingId(null)}
        onSaved={() => {
          dispatchToast(
            <Toast><ToastTitle>User saved</ToastTitle><ToastBody>Changes written and audit row recorded.</ToastBody></Toast>,
            { intent: "success" }
          );
          load();
        }}
        onDeleted={(displayName) => {
          dispatchToast(
            <Toast><ToastTitle>User deleted</ToastTitle><ToastBody>{displayName} was removed and a snapshot was written to the audit log.</ToastBody></Toast>,
            { intent: "success" }
          );
          load();
        }}
      />

      <AddUserDialog
        open={adding}
        activeUsers={data.users.filter((u) => u.isActive && !u.isExEmployee)}
        departments={data.departments}
        onClose={() => setAdding(false)}
        onCreated={(displayName) => {
          dispatchToast(
            <Toast>
              <ToastTitle>User added</ToastTitle>
              <ToastBody>{displayName} is now in the directory. Set their leave allocations from Team → Adjust allocations.</ToastBody>
            </Toast>,
            { intent: "success" }
          );
          load();
        }}
      />
    </div>
  );
}
